import { useMemo } from "react";
import type { ReactElement } from "react";
import { Skeleton, Stack, useMultiStyleConfig } from "@chakra-ui/react";
import type { SkeletonProps, StackProps } from "@chakra-ui/react";
import { getIndexArray } from "utils/array";

interface SkeletonTagProps extends SkeletonProps {
  /**
   * The `size` prop used to emulate the Chakra `Tag` styles
   */
  size?: "sm" | "md" | "lg";
}

/**
 * A Chakra `Skeleton` styled like a Chakra `Tag`
 */
export const SkeletonTag = ({
  size = "md",
  ...props
}: SkeletonTagProps): ReactElement => {
  const { container } = useMultiStyleConfig("Tag", { size });

  return <Skeleton {...props} sx={container} />;
};

interface SkeletonTagGroupItemProps {
  minFlex: number;
  maxFlex: number;
  size: "sm" | "md" | "lg";
}

const SkeletonTagGroupItem = ({
  minFlex = 8,
  maxFlex = 20,
  size = "md",
}: SkeletonTagGroupItemProps): ReactElement => {
  const flex = useMemo(
    () => Math.ceil(Math.random() * (maxFlex - minFlex) + minFlex),
    [maxFlex, minFlex]
  );

  return <SkeletonTag flex={flex} size={size} />;
};

interface SkeletonTagGroupProps extends StackProps {
  /**
   * The number of lines of tags to render.
   *
   * @defaultValue `4`
   */
  noOfLines?: number;

  /**
   * The number of tags to render per row.
   *
   * @defaultValue `4`
   */
  noOfTags?: number;

  /**
   * The spacing between each row and each tag in each row. Uses the `spacing` prop from the `Stack` component.
   *
   * @defaultValue `2`
   */
  spacing?: number;

  /**
   * The minimum `flex` value to apply to each tag in each row.  Each tag will be assigned a random flex value between `minFlex` and `maxFlex`.
   *
   * @defaultValue `8`
   */
  minFlex?: number;

  /**
   * The maximum `flex` value to apply to each tag in each row.  Each tag will be assigned a random flex value between `minFlex` and `maxFlex`.
   *
   * @defaultValue `20`
   */
  maxFlex?: number;

  /**
   * The size of the tags in the loader. Uses the `size` prop on the `Tag` component.
   *
   * @defaultValue `"md"`
   */
  size?: "sm" | "md" | "lg";
}

/**
 * A component which renders multiple rows of randomly sized skeletons that appear the same as a Chakra `Tag`.
 */
export const SkeletonTagGroup = ({
  noOfLines = 4,
  noOfTags = 4,
  spacing = 2,
  minFlex = 8,
  maxFlex = 20,
  size = "md",
  ...props
}: SkeletonTagGroupProps): ReactElement => (
  <Stack direction="column" spacing={spacing} {...props}>
    {getIndexArray(noOfLines).map((rowI) => (
      <Stack direction="row" spacing={spacing} key={`skeleton-row-${rowI}`}>
        {getIndexArray(noOfTags).map((tagI) => (
          <SkeletonTagGroupItem
            key={`skeleton-cell-${tagI}`}
            minFlex={minFlex}
            maxFlex={maxFlex}
            size={size}
          />
        ))}
      </Stack>
    ))}
  </Stack>
);
